import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import {
  FeatureEnum,
  imageMaskLayerVisibilityInitial,
  maskColorsInitial,
} from '../../services/data/impl/cube/cubeSlice';
import { ScanData } from '../scanDataApi/scanDataDefinitions';

export type ImageMaskVisibilityValues = Record<FeatureEnum, boolean>;

export type ImageIncrementDirection = 'forward' | 'back';

export type MaskColors = Record<FeatureEnum, string>;

export type CameraSelection = 'left' | 'right';

interface ColorCorrectionResult {
  finalClipLimit: number;
  error: boolean;
  isLoading: boolean;
}

interface ImageState {
  imageMaskLayerVisibility: ImageMaskVisibilityValues;
  currNeighborPoints: ScanData[]; // the current and neighboring points in the image viewer
  autoPlay: boolean;
  autoPlayDirection: ImageIncrementDirection;
  autoplaySpeed: number;
  autoPlayIncrement: number;
  showMasks: boolean;
  maskOpacity: number;
  maskColors: MaskColors;
  maskOutline: boolean;
  maskInstanceColors: boolean;
  displayUnknownMasks: boolean;
  imageBrightness: number;
  imageResolution: number;
  cameraSelection: CameraSelection;
  frontendColorCorrectionResult: ColorCorrectionResult;
  frontendColorCorrection: boolean;
  frontendColorCorrectionProcessDarkImagesOverride: boolean;
  backendColorCorrection: boolean;
  backendIntendedColorCorrection: boolean;
  showDepthMap: boolean;
  depthMapOpacity: number;
}

const initialState: ImageState = {
  imageMaskLayerVisibility: imageMaskLayerVisibilityInitial,
  currNeighborPoints: [],
  autoPlay: false,
  autoPlayDirection: 'forward',
  autoplaySpeed: 2000,
  autoPlayIncrement: 1,
  showMasks: true,
  maskOpacity: 50,
  maskColors: maskColorsInitial,
  maskOutline: true,
  maskInstanceColors: false,
  displayUnknownMasks: false,
  imageBrightness: 0,
  imageResolution: 720,
  cameraSelection: 'left',
  frontendColorCorrectionResult: {
    finalClipLimit: 0,
    error: false,
    isLoading: false,
  },
  frontendColorCorrection: true,
  frontendColorCorrectionProcessDarkImagesOverride: false,
  backendColorCorrection: false,
  backendIntendedColorCorrection: false,
  showDepthMap: false,
  depthMapOpacity: 75,
};

export const imageState = createSlice({
  name: 'imageState',
  initialState,
  reducers: {
    initImageState: () => initialState,
    setImageMaskLayerVisibility: (
      state,
      action: PayloadAction<{ layer: FeatureEnum; newValue: boolean }>
    ) => {
      state.imageMaskLayerVisibility[action.payload.layer] =
        action.payload.newValue;
    },
    setCurrNeighborPoints: (state, action: PayloadAction<ScanData[]>) => {
      state.currNeighborPoints = action.payload;
    },
    setAutoPlay: (state, action: PayloadAction<boolean>) => {
      state.autoPlay = action.payload;
    },
    setAutoPlayDirection: (
      state,
      action: PayloadAction<ImageIncrementDirection>
    ) => {
      state.autoPlayDirection = action.payload;
    },
    setAutoPlaySpeed: (state, action: PayloadAction<number>) => {
      state.autoplaySpeed = action.payload;
    },
    setAutoPlayIncrement: (state, action: PayloadAction<number>) => {
      state.autoPlayIncrement = action.payload;
    },
    setShowMasks: (state, action: PayloadAction<boolean>) => {
      state.showMasks = action.payload;
    },
    setMaskOpacity: (state, action: PayloadAction<number>) => {
      state.maskOpacity = action.payload;
    },
    setMaskColor: (
      state,
      action: PayloadAction<{ layer: FeatureEnum; newValue: string }>
    ) => {
      state.maskColors[action.payload.layer] = action.payload.newValue;
    },
    setMaskInstanceColors: (state, action: PayloadAction<boolean>) => {
      state.maskInstanceColors = action.payload;
    },
    setMaskOutline: (state, action: PayloadAction<boolean>) => {
      state.maskOutline = action.payload;
    },
    setDisplayUnknownMasks: (state, action: PayloadAction<boolean>) => {
      state.displayUnknownMasks = action.payload;
    },
    setImageBrightness: (state, action: PayloadAction<number>) => {
      state.imageBrightness = action.payload;
    },
    setImageResolution: (state, action: PayloadAction<number>) => {
      state.imageResolution = action.payload;
    },
    setCameraSelection: (state, action: PayloadAction<CameraSelection>) => {
      state.cameraSelection = action.payload;
    },
    setFrontendColorCollectionResult: (
      state,
      action: PayloadAction<ColorCorrectionResult>
    ) => {
      state.frontendColorCorrectionResult = action.payload;
    },
    setFrontendColorCorrection: (state, action: PayloadAction<boolean>) => {
      state.frontendColorCorrection = action.payload;
    },
    setFrontendColorCorrectionProcessDarkImagesOverride: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.frontendColorCorrectionProcessDarkImagesOverride = action.payload;
    },
    setBackendColorCorrection: (state, action: PayloadAction<boolean>) => {
      state.backendColorCorrection = action.payload;
    },
    setBackendIntendedColorCorrection: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.backendColorCorrection = action.payload;
    },
    setShowDepthMap: (state, action: PayloadAction<boolean>) => {
      state.showDepthMap = action.payload;
    },
    setDepthMapOpacity: (state, action: PayloadAction<number>) => {
      state.depthMapOpacity = action.payload;
    },
  },
});

export const {
  initImageState,
  setImageMaskLayerVisibility,
  setCurrNeighborPoints,
  setAutoPlay,
  setAutoPlayDirection,
  setAutoPlaySpeed,
  setAutoPlayIncrement,
  setShowMasks,
  setMaskOpacity,
  setMaskColor,
  setMaskOutline,
  setMaskInstanceColors,
  setDisplayUnknownMasks,
  setImageBrightness,
  setImageResolution,
  setCameraSelection,
  setFrontendColorCollectionResult,
  setFrontendColorCorrection,
  setFrontendColorCorrectionProcessDarkImagesOverride,
  setBackendColorCorrection,
  setBackendIntendedColorCorrection,
  setShowDepthMap,
  setDepthMapOpacity,
} = imageState.actions;

export const selectImageMaskVisibility = (state: RootState) =>
  state.image.imageMaskLayerVisibility;
export const selectCurrNeighborPoints = (state: RootState) =>
  state.image.currNeighborPoints;
export const selectAutoPlay = (state: RootState) => state.image.autoPlay;
export const selectAutoPlayDirection = (state: RootState) =>
  state.image.autoPlayDirection;
export const selectAutoPlaySpeed = (state: RootState) =>
  state.image.autoplaySpeed;
export const selectAutoPlayIncrement = (state: RootState) =>
  state.image.autoPlayIncrement;
export const selectShowMasks = (state: RootState) => state.image.showMasks;
export const selectMaskOpacity = (state: RootState) => state.image.maskOpacity;
export const selectMaskColors = (state: RootState) => state.image.maskColors;
export const selectMaskOutline = (state: RootState) => state.image.maskOutline;
export const selectMaskInstanceColors = (state: RootState) =>
  state.image.maskInstanceColors;
export const selectDisplayUnknownMasks = (state: RootState) =>
  state.image.displayUnknownMasks;
export const selectImageBrightness = (state: RootState) =>
  state.image.imageBrightness;
export const selectImageResolution = (state: RootState) =>
  state.image.imageResolution;
export const selectCameraSelection = (state: RootState) =>
  state.image.cameraSelection;
export const selectFrontendColorCorrectionResult = (state: RootState) =>
  state.image.frontendColorCorrectionResult;
export const selectFrontendColorCorrection = (state: RootState) =>
  state.image.frontendColorCorrection;
export const selectFrontendColorCorrectionProcessDarkImagesOverride = (
  state: RootState
) => state.image.frontendColorCorrectionProcessDarkImagesOverride;
export const selectBackendColorCorrection = (state: RootState) =>
  state.image.backendColorCorrection;
export const selectBackendIntendedColorCorrection = (state: RootState) =>
  state.image.backendIntendedColorCorrection;
export const selectShowDepthMap = (state: RootState) =>
  state.image.showDepthMap;
export const selectDepthMapOpacity = (state: RootState) =>
  state.image.depthMapOpacity;

export const selectAllImageState = (state: RootState) => state.image;

export default imageState.reducer;
